import { State, process } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo, useState } from 'react';
import { defaultExcelOption } from 'src/components/ExportExcel';
import GridWrapper, { GridColumn } from 'src/components/GridWrapper';
import GLCodeDescriptionCell from 'src/components/custom-cells/GLCodeDescriptionCell';
import useViewDepartment from 'src/hooks/useViewDepartments';
import { DepartmentGlCode } from 'src/types/department';
import exportToExcel from 'src/utils/exportToExcel';
import { sliceFiveChar } from 'src/utils/strings';
import DepartmentTableModalsToolbar from './DepartmentTableModalsToolbar';
interface Props {
  departmentGLCodes: DepartmentGlCode[];
  selectedDepartmentName: string;
}

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 4,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  excelFileName: 'GL Codes.xlsx',
  tableTextId: 'GL Codes',
  DataItemKey: 'id',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: gridPagerSettings,
} as const;

const initialDepartmentGLCodeDataState: State = {
  skip: 0,
  take: 10,
  sort: [{ field: 'code', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const glCodesColumns: GridColumn<any>[] = [
  {
    field: 'code',
    title: 'GL Code',
    filter: 'text',
    dataType: 'string',
    menu: true,
    sortable: true,
    width: '120px',
  },
  {
    field: 'description',
    title: 'Description',
    filter: 'text',
    dataType: 'string',
    menu: true,
    sortable: true,
    component: GLCodeDescriptionCell,
  },
];

const DepartmentGLCodesTable: FC<Props> = ({ departmentGLCodes, selectedDepartmentName }) => {
  const [state, setState] = useState<State>(initialDepartmentGLCodeDataState);
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const [excelLoading, setExcelLoading] = useState(false);
  const { t, selectedCompany, buId } = useViewDepartment();
  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setState(e.dataState);
  }, []);

  const onClearFilter = useCallback(() => {
    setState(initialDepartmentGLCodeDataState);
  }, []);

  const { data, total } = useMemo(
    () => process(departmentGLCodes, state),
    [departmentGLCodes, state]
  );

  const excelExport = useCallback(
    (data: DepartmentGlCode[]) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      columns.push('selectedDepartmentName');
      columnMapping['selectedDepartmentName' as string] = 'Department Name';

      glCodesColumns.forEach((item) => {
        columns.push(item.field as string);
        Object.assign(columnMapping, { [item.field as string]: item.title });
      });

      const rows = data.map((item) => ({
        selectedDepartmentName: selectedDepartmentName,
        ...item,
      }));

      exportToExcel({
        columns,
        rows,
        fileName: `${buId ? `${buId}_` : ''}${
          sliceFiveChar(selectedCompany?.companyName || '') +
          '_' +
          sliceFiveChar(selectedDepartmentName || '') +
          '_' +
          t('GLCodes') +
          '_'
        } ${new Date().toLocaleString().replace(',', ' ')}`,
        columnMapping,
      });
    },
    [buId, selectedCompany?.companyName, selectedDepartmentName, t]
  );

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      if (selectedOption.value === 1) {
        setExcelLoading(true);
        excelExport(data);
        setExcelOption(defaultExcelOption);
        setExcelLoading(false);
      } else if (selectedOption.value === 2) {
        const { data } = process(departmentGLCodes, { filter: state.filter });
        setExcelLoading(true);
        excelExport(data);
        setExcelOption(defaultExcelOption);
        setExcelLoading(false);
      }
    },
    [data, departmentGLCodes, excelExport, state.filter]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <DepartmentTableModalsToolbar
        onClearFilter={onClearFilter}
        dataState={state}
        excelOption={excelOption}
        handleExcelChange={handleExcelChange}
      />
    ),
    [excelOption, handleExcelChange, onClearFilter, state]
  );

  return (
    <GridWrapper
      id="departmentGLCodesTable"
      columns={glCodesColumns}
      dataSource={data}
      total={total}
      dataState={state}
      onDataStateChange={onDataStateChange}
      style={{ maxHeight: '70vh' }}
      {...defaultProps}
      noRecordText="No GL Codes to display"
      gridToolbarChildren={gridToolbarChildren}
      loading={excelLoading}
    />
  );
};

export default DepartmentGLCodesTable;
